/**
 * Hash chuỗi thành số 32-bit không dấu bằng thuật toán FNV-1a.
 * Cùng đầu vào luôn cho cùng kết quả (deterministic).
 */
function fnv1a32(str: string): number {
    let hash = 0x811c9dc5; // FNV offset basis
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        // Nhân 32-bit, giữ trong phạm vi unsigned 32-bit
        hash = (Math.imul(hash, 0x01000193) >>> 0);
    }
    return hash;
}

/**
 * Sinh mã màu hex (#rrggbb) từ một chuỗi bất kỳ.
 * Cùng chuỗi đầu vào → cùng màu đầu ra (bất kể lần gọi nào).
 *
 * Màu được chọn trong không gian HSL với saturation và lightness cố định
 * để đảm bảo kết quả luôn dễ nhìn và đủ tương phản.
 *
 * @param text - Chuỗi bất kỳ làm seed
 * @param saturation - Độ bão hoà (0–100), mặc định 60
 * @param lightness  - Độ sáng (0–100), mặc định 52
 * @returns Mã màu hex dạng `#rrggbb`
 *
 * @example
 * colorFromText('Phòng Đào tạo')   // '#4a90d9' (luôn cố định)
 * colorFromText('ABC')             // '#...'    (luôn cố định)
 */
export function colorFromText(
    text: string,
    saturation = 60,
    lightness = 52
): string {
    const hue = fnv1a32(text) % 360;
    return hslToHex(hue, saturation, lightness);
}

/**
 * Sinh cặp màu nền + chữ (foreground) từ một chuỗi bất kỳ.
 * Phù hợp dùng trực tiếp cho badge, avatar, tag.
 *
 * @param text - Chuỗi bất kỳ làm seed
 * @returns `{ bg: string; fg: string }` — cả hai đều là hex #rrggbb
 *
 * @example
 * const { bg, fg } = colorPairFromText('Nguyễn Văn A');
 * <span style={{ background: bg, color: fg }}>{text}</span>
 */
export function colorPairFromText(text: string): ColorPairFromTextResult {
    const hue = fnv1a32(text) % 360;
    const bg = hslToHex(hue, 55, 90); // nền nhạt
    const fg = hslToHex(hue, 60, 30); // chữ đậm cùng tone
    return { bg, fg };
}

/** Chuyển HSL → hex #rrggbb */
function hslToHex(h: number, s: number, l: number): string {
    const sn = s / 100;
    const ln = l / 100;
    const a = sn * Math.min(ln, 1 - ln);
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = ln - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
        return Math.round(255 * color)
            .toString(16)
            .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

export type ColorPairFromTextResult = {
    bg: string; fg: string
}